<template>
  <div class="file-uploader">
    <div v-if="showPreview" class="main-file-placeholder mb-2">
      <img
        v-if="activeItemIndex === null || !itemsProp[activeItemIndex] || (!base64UploadMode && itemsProp[activeItemIndex].processing)"
        class="img-fluid" src='@/assets/no-image.jpg'>
      <template v-if="activeItemIndex !== null && itemsProp[activeItemIndex] && !itemsProp[activeItemIndex].processing">
        <img v-if="isType(itemsProp[activeItemIndex], 'image')" :src="getSrc(itemsProp[activeItemIndex], 'main')"
             class="img-fluid">
        <b-embed v-if="isType(itemsProp[activeItemIndex], 'video')" controls allowfullscreen
                 :src="getSrc(itemsProp[activeItemIndex],'video')"/>
      </template>
    </div>
    <div class="mt-2 mb-2" v-if="uploadingProgress">
      <b-progress class="mb-2 mt-2" animated striped variant="primary">
        <b-progress-bar :value="uploadingProgressValue"
                        :label="$t('Uploading files(s) - {progressValue}%', {progressValue: uploadingProgressValue})"/>
      </b-progress>
      <h5 class="text-center" v-if="inProgress">{{ progressText }}</h5>
    </div>
    <div v-if="itemsProp.length || !hideUploadButton" @dragenter="onDragEnter" @dragleave="onDragLeave" @dragover.prevent @drop="onDrop"
         :class="{ dragging: isDragging}"
         class="file-uploader">
      <div v-if="imagesResizing" class="images-resizing-loader">
        <div>
          <div>{{$t('We are processing your images')}}</div>
          <div>{{$t('Processing {number}/{total}', {number: this.imagesResizing, total: this.numberOfChosenImages})}}...</div>
        </div>
      </div>
      <div v-show="!itemsProp.length && !hideUploadButton">
        <font-awesome-icon :icon="'cloud-upload-alt'" id="upload-icon"></font-awesome-icon>
        <div>{{ text }}</div>
        <div>{{ $t('OR') }}</div>
        <div class="d-flex">
          <div class="file-input" v-if="enablePictureTaking" :class="{'mr-2': enablePictureTaking}">
            <label class="hover-cursor" @click="onTakePictureClick">{{ takePictureButtonText }}</label>
          </div>
          <div class="file-input" :class="{'ml-2': enablePictureTaking}">
            <label :for="`file-uploader-input-${uuid}`">{{ buttonText }}</label>
            <input type="file" :id="`file-uploader-input-${uuid}`" @change="onInputChange" ref="inputRef"
                   :accept="computedAccept.join(',')" :multiple="multiple">
          </div>
        </div>

      </div>
      <div class="d-flex align-items-center space buttons-container"
           v-if="itemsProp.length && multiple && !hideActionButtons">
        <b-button v-if="!hideDeleteButton" size="sm" variant="danger" class="ml-auto mr-2" @click="removeAll($event)">
          {{ $t('Delete All') }}
        </b-button>
        <b-button :disabled="activeItemIndex === null" size="sm" variant="light" class="mr-2"
                  @click="onImageEditClick" v-if="enableImageEdit">
          {{ $t('Edit') }}
        </b-button>
        <b-button size="sm" variant="light" @click="$refs.inputRef.click()" class="mr-2">
          {{ $t('Select File(s)') }}
        </b-button>
        <b-button size="sm" variant="light" @click="onTakePictureClick" v-if="enablePictureTaking">
          {{ $t('Take Picture') }}
        </b-button>
      </div>

      <draggable v-if="isDraggable && multiple" @change="onDragChange" v-show="filteredImages.length"
                 class="files-preview" :list="filteredImages" @start="drag=true" @end="drag=false">

        <div v-if="!item.hidden" @click="onThumbnailClick(item, index)" class="thumbnail-wrapper"
             v-for="(item, index) in filteredImages"
             :key="index">
          <div v-if="!(item.processing === true) || base64UploadMode && item.base64" class="file-inner-wrapper"
               :class="{active: activeItemIndex === index}">
            <template v-if="!hideImageActions">
              <b-tooltip :target="`notify-btn-${index}`" :title="computedNotificationTooltip(item)"/>
              <font-awesome-icon :id="`notify-btn-${index}`" class="notify-btn"
                                 :class="{'d-none': !item.to_be_deleted && item.uuid}" :icon="'exclamation-circle'"/>
              <font-awesome-icon v-if="item.to_be_deleted" class="remove-btn undo-btn" icon="undo"
                                 @click="onUndoClick($event,index)"/>
              <font-awesome-icon v-else @click="onRemoveClick($event,index)"
                                 class="remove-btn" icon="times-circle"/>
            </template>
            <b-tooltip :target="`thumb-${index}`" :title="getImageDisplayName(item)"
                       :disabled="!getImageDisplayName(item)"/>
            <div :id="`thumb-${index}`" class="file-placeholder"
                 :class="{'transparent': item.to_be_deleted || !item.uuid}">
              <img v-if="isType(item, 'image')" :src="getSrc(item, 'thumb')"
                   class="img-fluid"/>
              <font-awesome-icon style="font-size: 3em" v-else-if="isType(item, 'video')" icon="file-video"/>
              <font-awesome-icon style="font-size: 3em" v-else-if="isType(item, 'audio')" icon="file-audio"/>
              <font-awesome-icon style="font-size: 3em" v-else-if="isType(item, 'pdf')" icon="file-pdf"/>
            </div>
          </div>
          <div v-if="item.processing === true" class="file-inner-wrapper">
            <font-awesome-icon icon="spinner" spin style="font-size: 3em"/>
          </div>
        </div>
      </draggable>

      <draggable v-else-if="isDraggable && !multiple" @change="onDragChange" v-show="itemsProp.length"
                 class="files-preview" :list="itemsProp" @start="drag=true" @end="drag=false">
        <div @click="onThumbnailClick(item, index)" class="thumbnail-wrapper" v-for="(item, index) in itemsProp"
             :key="index">
          <div v-if="!(item.processing === true) || base64UploadMode" class="file-inner-wrapper">
            <template v-if="!hideImageActions">
              <font-awesome-icon v-if="item.to_be_deleted" class="remove-btn undo-btn" icon="undo"
                                 @click="onUndoClick($event,index)"/>
              <font-awesome-icon v-else @click="onRemoveClick($event,index)"
                                 class="remove-btn" icon="times-circle"/>
            </template>
            <b-tooltip :target="`thumb-single-${index}`" :title="getImageDisplayName(item)"
                       :disabled="!getImageDisplayName(item)"/>
            <div :id="`thumb-single-${index}`" class="file-placeholder">
              <img v-if="isType(item, 'image')" :src="getSrc(item, 'thumb-draggable-single')" class="img-fluid"/>
              <font-awesome-icon style="font-size: 3em" v-else-if="isType(item, 'video')" icon="file-video"/>
              <font-awesome-icon style="font-size: 3em" v-else-if="isType(item, 'audio')" icon="file-audio"/>
              <font-awesome-icon style="font-size: 3em" v-else-if="isType(item, 'pdf')" icon="file-pdf"/>
            </div>
          </div>
          <div v-if="item.processing === true" class="file-inner-wrapper">
            <font-awesome-icon icon="spinner" spin style="font-size: 3em"/>
          </div>
        </div>
      </draggable>

      <div v-else @change="onDragChange" v-show="itemsProp.length" class="files-preview">
        <div @click="onThumbnailClick(item, index)" class="thumbnail-wrapper" v-for="(item, index) in itemsProp"
             :key="index">
          <div v-if="!(item.processing === true) || base64UploadMode" class="file-inner-wrapper"
               :class="{active: activeItemIndex === index}">
            <template v-if="!hideImageActions">
              <b-tooltip :target="`notify-btn-${index}`" :title="computedNotificationTooltip(item)"/>
              <font-awesome-icon :id="`notify-btn-${index}`" class="notify-btn"
                                 :class="{'d-none': !item.to_be_deleted && item.uuid}" :icon="'exclamation-circle'"/>
              <font-awesome-icon v-if="item.to_be_deleted" class="remove-btn undo-btn" icon="undo"
                                 @click="onUndoClick($event,index)"/>
              <font-awesome-icon v-else @click="onRemoveClick($event,index)"
                                 class="remove-btn" icon="times-circle"/>
            </template>
            <b-tooltip :target="`thumb-static-${index}`" :title="getImageDisplayName(item)"
                       :disabled="!getImageDisplayName(item)"/>
            <div :id="`thumb-static-${index}`" class="file-placeholder"
                 :class="{'transparent': item.to_be_deleted || !item.uuid}">
              <img v-if="isType(item, 'image')" :src="getSrc(item, 'thumb')"
                   class="img-fluid"/>
              <font-awesome-icon style="font-size: 3em" v-else-if="isType(item, 'video')" icon="file-video"/>
              <font-awesome-icon style="font-size: 3em" v-else-if="isType(item, 'audio')" icon="file-audio"/>
              <font-awesome-icon style="font-size: 3em" v-else-if="isType(item, 'pdf')" icon="file-pdf"/>
            </div>
          </div>
          <div v-if="item.processing === true" class="file-inner-wrapper">
            <font-awesome-icon icon="spinner" spin style="font-size: 3em"/>
          </div>
        </div>
      </div>
    </div>
    <b-form-text v-if="hintText && (itemsProp.length || !hideUploadButton)">{{ hintText }}</b-form-text>
    <image-editor-modal ref="imageEditorModal" @onSaveClick="onImageEditorSaveClick"/>
  </div>
</template>

<script>
  import draggable from 'vuedraggable'
  import {Utils} from "../../services/utils.service";
  import i18n from "@/shared/i18n";
  import {uuid} from 'vue-uuid';
  import EventBus from "@/plugins/event-bus";
  import {
    ON_CAMERA_MODAL_OPEN,
    ON_CLEAR_FILE_UPLOADER_INPUT
  } from "@/plugins/event-bus/event-bus-names";
  import ImageEditorModal from "@/core/components/image-editor/ImageEditorModal";
  import InventoryService from "@/modules/inventory/InventoryService";
  import {convertToHeic} from "../../helpers";

  /**
   * @author Saiat Kalbiev <kalbievich11@gmail.com>
   */
  export default {
    name: 'FileUploader',
    components: {
      ImageEditorModal,
      draggable,
    },
    props: {
      disableImageDragUpload: {
        type: Boolean,
        default: false
      },
      hideActionButtons: {
        type: Boolean,
        default: false
      },
      hideDeleteButton: {
        type: Boolean,
        default: false
      },
      hideUploadButton: {
        type: Boolean,
        default: false
      },
      hideImageActions: {
        type: Boolean,
        default: false
      },
      isDraggable: {
        type: Boolean,
        default: true
      },
      multiple: {
        type: Boolean,
        default: true
      },
      text: {
        type: String,
        default: i18n.t('Drag your file(s) here')
      },
      progressText: {
        type: String,
        default: i18n.t('Please wait... Files are being processed')
      },
      buttonText: {
        type: String,
        default: i18n.t('Select file(s)')
      },
      takePictureButtonText: {
        type: String,
        default: i18n.t('Take Picture')
      },
      enablePictureTaking: {
        type: Boolean,
        default: false
      },
      hintText: {
        type: [String, Boolean],
        default: i18n.t('Click, hold and drag images to change their ordering')
      },
      inProgress: {
        type: Boolean,
        default: false
      },
      uploadingProgress: {
        type: Boolean,
        default: false
      },
      uploadingProgressValue: {
        type: Number,
        default: 0
      },
      itemsProp: {
        type: Array,
        default: () => []
      },
      accept: {
        type: Array,
        default: () => [] // Ex: [ {types: ['image/png'], size: 10} ]
      },
      showPreview: {
        type: Boolean,
        default: true
      },
      base64UploadMode: {
        type: Boolean,
        default: false
      },
      enableImageEdit: {
        type: Boolean,
        default: false
      },
      enableImageResize: {
        type: Boolean,
        default: false
      },
      limitUploadNumber: {
        type: Number,
        default: 0 // 0 means no limit
      },
      limitUploadTotalSize: {
        type: Number,
        default: 0
      },
    },
    data() {
      return {
        uuid: uuid.v4(),
        isDragging: false,
        dragCount: 0,
        activeItemIndex: null,
        imagesResizing: false,
        numberOfChosenImages: 0,
      }
    },
    computed: {
      computedAccept() {
        return this.accept.reduce((prev, curr) => [...prev, ...curr.formats], []);
      },
      filteredImages() {
        return this.itemsProp
      }
    },
    methods: {
      clearInput() {
        if (this.$refs.inputRef) {
          this.$refs.inputRef.value = null;
        }
      },
      async onInputChange($event) {
        await this.processFiles($event.target.files)
      },
      onDragEnter($event) {
        if (this.disableImageDragUpload) {
          return
        }
        $event.preventDefault();
        this.isDragging = true;
        this.dragCount++;
      },
      onDragLeave($event) {
        if (this.disableImageDragUpload) {
          return
        }
        $event.preventDefault();
        this.dragCount--;
        if (this.dragCount <= 0) {
          this.isDragging = false;
        }
      },
      async onDrop($event) {
        if (this.disableImageDragUpload) {
          return
        }
        if (this.isDragging) {
          $event.preventDefault();
          $event.stopPropagation();
          this.isDragging = false;

          await this.processFiles($event.dataTransfer.files)
        }
      },
      processFiles(files){
        if (this.limitUploadNumber && this.limitUploadNumber < files.length + this.itemsProp.length) {
          this.$errorDialog(
            this.$i18n.t("You can not upload more than {count} files.", { count: this.limitUploadNumber }),
            this.$i18n.t('Attachments')
          );
          return;
        }
        if (this.limitUploadTotalSize) {
          let totalSize = Array.from(files).reduce((sum, item) => (item.size ? sum + item.size : sum), 0);
          totalSize += this.itemsProp.reduce((sum, item) => (item.size ? sum + item.size : sum), 0);
          totalSize /= 1000000;
          if (totalSize > this.limitUploadTotalSize) {
            this.$errorDialog(
              this.$i18n.t("Total allowed file size is {size} MBs.", { size: this.limitUploadTotalSize }),
              this.$i18n.t('Images')
            );
            return;
          }
        }
        if (this.enableImageResize) {
          const images = [...files].filter(f => f.type.indexOf('image/') === 0);
          const otherFiles = [...files].filter(f => f.type.indexOf('image/') !== 0);
          if (otherFiles.length > 0){
            this.addFiles(otherFiles)
          }
          this.resizeImages(images);
        } else {
          this.addFiles(files)
        }
      },
      async resizeImages(files) {
        this.numberOfChosenImages = files.length;
        this.imagesResizing = 0;

        const returnFiles = [];
        for (let file of files) {
          this.imagesResizing++;
          const f = await convertToHeic(file);
          returnFiles.push(f);
        }
        await this.addFiles(returnFiles)

        this.imagesResizing = false;
        this.numberOfChosenImages = 0;

        return returnFiles;
      },
      async addFiles(files) {
        const failedFilesData = [];
        const successfulFiles = [];

        if (!this.multiple && this.itemsProp.length === 1) {
          this.$errorDialog(this.$i18n.t('Only single file is allowed'), this.$i18n.t('Uploader'));
          return;
        }

        Array.from(files).forEach(file => {
          this.checkFile(failedFilesData, successfulFiles, file);
        });

        if (failedFilesData.length === 0) {
          const promises = [];
          successfulFiles.forEach(file => promises.push(this.addItem(file)))
          this.$emit('onItemsAdd', await Promise.all(promises));
        } else {
          Utils.parseFailedImagesData(failedFilesData, this);
        }
      },
      onThumbnailClick(item, index) {
        this.activeItemIndex = index;
      },
      onRemoveClick(event, index) {
        event.stopPropagation();
        this.removeByIndex(index)
      },
      removeByIndex(index, hidden = false) {
        const uuid = this.itemsProp[index].uuid;
        if (!uuid) {
          this.itemsProp.splice(index, 1);
        } else {
          this.itemsProp[index].to_be_deleted = true;
          this.itemsProp[index].hidden = hidden;
          this.$set(this.itemsProp, index, {...this.itemsProp[index]});
          if (this.base64UploadMode) {
            this.$emit('onItemRemove', index, false);
          } else {
            this.$emit('onItemRemove', {index, uuid});
          }
        }
        if (!uuid && !this.multiple) {
          this.$emit('onItemRemove', {index, uuid})
        }
        this.clearInput();
      },
      onUndoClick(event, index) {
        event.stopPropagation();
        this.itemsProp[index].to_be_deleted = false;
        this.$set(this.itemsProp, index, {...this.itemsProp[index]});
        this.clearInput();
      },
      removeAll(event) {
        let uniqueIdentifiersToDelete = this.itemsProp.filter(i => i.unique_identifier).map(i => i.unique_identifier)

        // deleting items that are already uploaded and have valid uuid from API
        for (let index in this.itemsProp) {
          event.stopPropagation();
          const uuid = this.itemsProp[index].uuid;

          if (uuid) {
            this.itemsProp[index].to_be_deleted = true;
            this.$set(this.itemsProp, index, {...this.itemsProp[index]});

            if (this.base64UploadMode) {
              let toBeDeleted = null;
              let deleteAll = true;
              this.$emit('onItemRemove', toBeDeleted, deleteAll);
            }
          }
        }

        // deleting items which are temporary and are not uploaded to s3 yet
        for (let uniqueIdentifier of uniqueIdentifiersToDelete) {
          let index = this.itemsProp.findIndex(i => i.unique_identifier === uniqueIdentifier)
          if (index >= 0) {
            this.itemsProp.splice(index, 1);
          }
        }

        this.clearInput();
      },
      onDragChange() {
        this.$emit('onImageDragChange', this.itemsProp);
      },
      addItem(file) {
        return new Promise((resolve) => {
          let fileReader = new FileReader();
          fileReader.onload = async (e) => {
            let item;

            if (this.base64UploadMode) {
              item = {
                file: {type: file.type, name: file.name},
                base64: e.target.result,
                blobPromise: InventoryService.convertBase64ToBlob(e.target.result),
                uuid: null,
                to_be_deleted: false,
                unique_identifier: uuid.v4()
              };
            } else {
              item = {
                file,
                base64_src: e.target.result,
                blobPromise: InventoryService.convertBase64ToBlob(e.target.result),
                uuid: null,
                processing: false,
                to_be_deleted: false,
                unique_identifier: uuid.v4()
              };
            }

            this.itemsProp.push(item);
            this.$emit('onItemAdd', item);
            resolve(item);
          };
          fileReader.readAsDataURL(file);
        });
      },
      computedNotificationTooltip(item) {
        if (item.to_be_deleted) {
          return this.$i18n.t('To be deleted')
        } else if (!item.uuid) {
          return this.$i18n.t('To be uploaded')
        }
        return '';
      },
      getImageDisplayName(item) {
        if (item.original_filename) {
          return item.original_filename;
        }
        if (item.file && item.file.name) {
          return item.file.name;
        }
        return '';
      },
      isType(item, type) {
        let contentType;

        if (this.base64UploadMode && item.base64) {
          contentType = this.getBase64MimeType(item.base64);
          return contentType && contentType.includes(type)
        }

        if (item.file) {
          contentType = item.file.type;
        } else if (item.content_type) {
          contentType = item.content_type
        } else {
          if (item.data) {
            if (item.data.main && type === 'video') {
              contentType = item.data.main.content_type;
            } else if (item.data && item.data.thumb && type === 'image') {
              contentType = item.data.thumb.content_type;
            } else if (item.content_type && type === 'pdf') {
              contentType = item.content_type;
            }
          }
        }

        return contentType && contentType.includes(type);
      },
      checkFile(failedFilesData, successfulFiles, file) {
        const sizeInMbs = file.size / 1000000; // Convert bytes to megabytes

        if (this.computedAccept && !this.computedAccept.includes(file.type)) {
          failedFilesData.push({failure_reason: 'type', file});
          return;
        }

        for (let obj of this.accept) {
          if (obj.formats.includes(file.type) && sizeInMbs > obj.maxSize) {
            failedFilesData.push({failure_reason: 'size', file, maxSize: obj.maxSize});
            return;
          }
        }

        successfulFiles.push(file);
      },
      getBase64MimeType(str) {
        let mime = str.match(/data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+).*,.*/);
        return mime && mime.length ? mime[1] : null;
      },
      getSrc(item, type) {
        if (this.base64UploadMode && item.base64) {
          return item.base64;
        } else {
          if (type === 'main') {
            if (!item.uuid) {
              return item.base64_src
            }
            return item && item.data && item.data.sm && item.data.sm.url;
          } else if (type === 'thumb') {
            if (!item.uuid) {
              return item.base64_src
            } else {
              if (item.data && item.data.thumb) {
                return item.data.thumb.url
              }
              return item.url || item.presigned_url;
            }
          } else if (type === 'video') {
            return item.data.main.url;
          } else if (type === 'thumb-draggable-single') {
            return item.base64_src ? item.base64_src : item.url;
          }
        }
      },
      onImageEditClick() {
        this.$refs.imageEditorModal.showModal(this.itemsProp[this.activeItemIndex])
      },
      onImageEditorSaveClick(item) {
        this.removeByIndex(this.activeItemIndex, true)
        this.itemsProp.splice(this.activeItemIndex, 0, item)
      },
      onTakePictureClick() {
        this.$evBus.$emit(ON_CAMERA_MODAL_OPEN, {
          emitKey: `on-finish-picture-${this._uid}`
        });
      }
    },
    beforeMount() {
      EventBus.$on(ON_CLEAR_FILE_UPLOADER_INPUT, () => {
        this.clearInput();
      });
      EventBus.$on(`on-finish-picture-${this._uid}`, async (data) => {
        await this.processFiles([data.file])
      });
    },
    beforeDestroy() {
      EventBus.$off(ON_CLEAR_FILE_UPLOADER_INPUT);
      EventBus.$off(`on-finish-picture-${this._uid}`);
    }
  };
</script>

<style lang="scss" src="./index.scss" scoped>

</style>
